import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshOutlined';
import { Tab, Tabs } from '@mui/material';

import { fetchDashboardWidgets } from '~/redux/actions/dashboard-actions';

import { chartColors, DASHBOARD_DATE_OPTIONS } from '~/utils/constants';
import { dateFormat } from '~/utils/date-format';
import OlcButton from '~/components/General/OlcButton';
import OlcDataGrid from '~/components/General/OlcDataGrid';
import OlcDatePicker from '~/components/General/OlcDatePicker';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import OlcTooltip from '~/components/General/OlcTooltip';
import { ContactInfoCIcon } from '~/assets/images';
import GettingStarted from './GettingStarted';

import './styles.scss';

const PRODUCT_COLORS = Object.values(chartColors);
const DASHBOARD_PAGE_SIZE = 5;
const SAVED_COST_PER_INVALID_ADDRESS = 0.63;
const ACTIVE_ORDER_STATUSES = new Set(['processing', 'mailed']);
const DASHBOARD_TOOLTIP_CLASSES = { tooltip: 'dashboard-tooltip' };
const GETTING_STARTED_HIDDEN_STORAGE_KEY = 'dashboard:getting-started-hidden';

const getIsGettingStartedHidden = () => {
  try {
    return window.localStorage.getItem(GETTING_STARTED_HIDDEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const numberFormatter = new Intl.NumberFormat('en-US');
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const formatNumber = (value) => numberFormatter.format(Number(value) || 0);
const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);
const getReturnRateLevel = (rate) => {
  if (rate < 5) return 'Low';
  if (rate < 10) return 'Normal';
  return 'High';
};
const formatAverageValue = (value) => {
  const days = Number(value);
  return Number.isFinite(days) ? days.toFixed(1) : '—';
};
const formatAverageDays = (value) => {
  const days = Number(value);
  if (!Number.isFinite(days)) return '—';

  return `${days.toFixed(1)} ${days === 1 ? 'day' : 'days'}`;
};
const formatDateForDuration = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatCustomDateLabel = (startDate, endDate) => {
  const monthDayFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const sameYear = startYear === endYear;
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  if (sameMonth) {
    return `${monthDayFormatter.format(startDate)}–${endDate.getDate()}, ${endYear}`;
  }

  if (sameYear) {
    return `${monthDayFormatter.format(startDate)}–${monthDayFormatter.format(endDate)}, ${endYear}`;
  }

  return `${monthDayFormatter.format(startDate)}, ${String(startYear).slice(-2)}–${monthDayFormatter.format(endDate)}, ${String(endYear).slice(-2)}`;
};

const humanize = (value = '') =>
  String(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getStatusTone = (status = '') => {
  const normalized = status.toLowerCase();
  if (normalized.includes('scheduled')) return 'scheduled';
  if (normalized.includes('deliver') || normalized.includes('complete')) return 'success';
  if (normalized.includes('mail')) return 'mailed';
  if (normalized.includes('transit')) return 'info';
  if (
    normalized.includes('production') ||
    normalized.includes('process') ||
    normalized.includes('pending')
  )
    return 'production';
  if (normalized.includes('return')) return 'danger';
  if (normalized.includes('cancel')) return 'warning';
  return 'neutral';
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dashboardWidgets = useSelector((state) => state.dashboardReducers.widgets);
  const isLiveMode = useSelector((state) => state.userReducers.user?.isLiveMode === true);
  const hasCachedDashboardData = dashboardWidgets?.mailPerformance != null;
  const [selectedDate, setSelectedDate] = useState(DASHBOARD_DATE_OPTIONS[4]);
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(() => !hasCachedDashboardData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGettingStartedHidden, setIsGettingStartedHidden] = useState(getIsGettingStartedHidden);
  const [ordersView, setOrdersView] = useState('active');
  const [performanceView, setPerformanceView] = useState('templates');
  const [ordersPage, setOrdersPage] = useState(1);
  const [performancePage, setPerformancePage] = useState(1);
  const hasLoadedDashboardRef = useRef(hasCachedDashboardData);
  const hadCachedDashboardDataOnMountRef = useRef(hasCachedDashboardData);
  const activeDashboardRequestKeyRef = useRef(null);

  const hideGettingStarted = () => {
    setIsGettingStartedHidden(true);

    try {
      window.localStorage.setItem(GETTING_STARTED_HIDDEN_STORAGE_KEY, 'true');
    } catch {
      // Keep the in-memory preference when browser storage is unavailable.
    }
  };

  const showGettingStarted = () => {
    setIsGettingStartedHidden(false);

    try {
      window.localStorage.removeItem(GETTING_STARTED_HIDDEN_STORAGE_KEY);
    } catch {
      // The guide can still be reopened when browser storage is unavailable.
    }
  };

  const mailData = dashboardWidgets?.mailPerformance;
  const loadDashboardData = useCallback(
    async (refreshCache = false) => {
      const duration = selectedDate.duration || selectedDate.title.toLowerCase();
      const requestKey = `${duration}:${refreshCache ? 'refresh' : 'load'}`;

      // React StrictMode runs mount effects twice in development. Reuse the
      // active request instead of fetching and rendering the dashboard twice.
      if (activeDashboardRequestKeyRef.current === requestKey) return;
      activeDashboardRequestKeyRef.current = requestKey;

      if (refreshCache) {
        setIsRefreshing(true);
      } else if (!hasLoadedDashboardRef.current) {
        setIsLoading(true);
      }

      try {
        await dispatch(
          fetchDashboardWidgets({
            duration,
            ...(refreshCache ? { refreshCache: true } : {}),
          })
        );
        hasLoadedDashboardRef.current = true;
      } finally {
        // A newer duration may have started while this request was in flight.
        // Only the latest request owns the visible loading state.
        if (activeDashboardRequestKeyRef.current === requestKey) {
          activeDashboardRequestKeyRef.current = null;
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [dispatch, selectedDate]
  );

  useEffect(() => {
    loadDashboardData(true);
  }, [loadDashboardData]);

  const delivered = Number(mailData?.delivered) || 0;
  const inTransit = Number(mailData?.inTransit) || 0;
  const returned = Number(mailData?.returnedToSender) || 0;
  const mailed = Number(mailData?.mailed) || 0;
  const mailPiecesSent = Number(mailData?.mailPiecesSent) || 0;
  const removedBeforeDelivery = Number(mailData?.removedBeforeDelivery) || 0;
  const statusTotal = delivered + inTransit + mailed + returned;
  const progressDenominator = Math.max(mailPiecesSent, statusTotal, 1);
  const deliveredPercent = (delivered / progressDenominator) * 100;
  const transitPercent = (inTransit / progressDenominator) * 100;
  const returnedPercent = (returned / progressDenominator) * 100;
  const mailedPercent = (mailed / progressDenominator) * 100;
  const returnRate = mailPiecesSent ? (returned / mailPiecesSent) * 100 : 0;
  const returnRateLevel = getReturnRateLevel(returnRate);
  const estimatedAddressSavings = removedBeforeDelivery * SAVED_COST_PER_INVALID_ADDRESS;
  const deliverySpeed = mailData?.deliverySpeed;
  const deliverySpeedPieces = Number(deliverySpeed?.overall?.pieces) || 0;

  const dateOptions = useMemo(() => {
    const customOption = {
      id: 'custom-date',
      title: 'Custom date',
      func: () => {
        setCustomDateRange([null, null]);
        setIsCustomDateOpen(true);
      },
    };

    return selectedDate.duration
      ? [selectedDate, ...DASHBOARD_DATE_OPTIONS, customOption]
      : [...DASHBOARD_DATE_OPTIONS, customOption];
  }, [selectedDate]);

  const applyCustomDate = ([startDate, endDate]) => {
    if (!startDate || !endDate) return;

    setSelectedDate({
      id: 'selected-custom-date',
      title: formatCustomDateLabel(startDate, endDate),
      duration: `custom ${formatDateForDuration(startDate)} ${formatDateForDuration(endDate)}`,
    });
    setIsCustomDateOpen(false);
  };

  const productMix = useMemo(() => {
    const entries = Object.entries(mailData?.productMix || {})
      .map(([name, value]) => ({ name: humanize(name), value: Number(value) || 0 }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = Math.max(mailPiecesSent, 1);
    let cursor = 0;
    const chartStops = entries.map((item, index) => {
      const percentage = total ? (item.value / total) * 100 : 0;
      const stop = `${PRODUCT_COLORS[index % PRODUCT_COLORS.length]} ${cursor}% ${
        cursor + percentage
      }%`;
      cursor += percentage;
      return stop;
    });

    return {
      entries: entries.map((item, index) => ({
        ...item,
        color: PRODUCT_COLORS[index % PRODUCT_COLORS.length],
        percentage: total ? (item.value / total) * 100 : 0,
      })),
      background: chartStops.length ? `conic-gradient(${chartStops.join(', ')})` : 'none',
    };
  }, [mailData?.productMix, mailPiecesSent]);

  const activeOrders = useMemo(
    () =>
      (dashboardWidgets?.activeOrders || [])
        .filter((order) => ACTIVE_ORDER_STATUSES.has(String(order.status).toLowerCase()))
        .map((order) => ({
          ...order,
          mailingDate: order.mailingDate
            ? dateFormat(order.mailingDate, true, true)
            : 'Not yet mailed',
        })),
    [dashboardWidgets?.activeOrders]
  );
  const futureOrders = useMemo(
    () =>
      (dashboardWidgets?.futureOrders || []).map((order) => ({
        ...order,
        mailingDate: order.mailingDate
          ? dateFormat(order.mailingDate, true, true)
          : 'Not yet scheduled',
      })),
    [dashboardWidgets?.futureOrders]
  );
  const selectedOrders = ordersView === 'future' ? futureOrders : activeOrders;
  const ordersPageCount = Math.max(1, Math.ceil(selectedOrders.length / DASHBOARD_PAGE_SIZE));
  const visibleOrders = useMemo(() => {
    const start = (ordersPage - 1) * DASHBOARD_PAGE_SIZE;
    return selectedOrders.slice(start, start + DASHBOARD_PAGE_SIZE);
  }, [ordersPage, selectedOrders]);
  const rankedPerformanceRows = useMemo(
    () =>
      (dashboardWidgets?.mostMailed?.[performanceView] || []).map((row) => ({
        ...row,
        id: `${performanceView}-${row.id}`,
        entityId:
          performanceView === 'orders'
            ? `Order #${row.id}`
            : `${performanceView === 'campaigns' ? 'Campaign #' : 'ID: '}${row.id}`,
        mailed: row.mailedAt ? dateFormat(row.mailedAt, true, true) : 'Not yet mailed',
      })),
    [dashboardWidgets?.mostMailed, performanceView]
  );
  const performancePageCount = Math.max(
    1,
    Math.ceil(rankedPerformanceRows.length / DASHBOARD_PAGE_SIZE)
  );
  const mostMailedRows = useMemo(() => {
    const start = (performancePage - 1) * DASHBOARD_PAGE_SIZE;
    return rankedPerformanceRows.slice(start, start + DASHBOARD_PAGE_SIZE);
  }, [performancePage, rankedPerformanceRows]);

  useEffect(() => {
    if (ordersPage > ordersPageCount) setOrdersPage(ordersPageCount);
  }, [ordersPage, ordersPageCount]);

  useEffect(() => {
    if (performancePage > performancePageCount) setPerformancePage(performancePageCount);
  }, [performancePage, performancePageCount]);

  const orderColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Order',
        minWidth: 220,
        flex: 1.6,
        sortable: false,
        compactValueGetter: (row) =>
          `${row.name || humanize(row.productType) || `Order ${row.id}`} ID ${row.id}`,
        renderCell: ({ row }) => (
          <div className="dashboard-primary-cell">
            <strong>{row.name || humanize(row.productType) || `Order ${row.id}`}</strong>
            <small>ID: {row.id}</small>
          </div>
        ),
      },
      {
        field: 'mailingDate',
        headerName: 'Mailing date',
        minWidth: 150,
        flex: 1,
        sortable: false,
        compactValueGetter: (row) => row.mailingDate || row.scheduledDate || 'Not yet mailed',
        renderCell: ({ row }) => row.mailingDate || row.scheduledDate || 'Not yet mailed',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 130,
        flex: 0.8,
        sortable: false,
        renderCell: ({ row }) => (
          <span className={`status-badge ${getStatusTone(row.status)}`}>
            {humanize(row.status)}
          </span>
        ),
      },
      {
        field: 'quantity',
        headerName: 'Quantity',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: false,
        compactValueGetter: (row) => formatNumber(row.quantity),
        renderCell: ({ row }) => formatNumber(row.quantity),
      },
      {
        field: 'cost',
        headerName: 'Spend',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: false,
        compactValueGetter: (row) => formatCurrency(row.cost),
        renderCell: ({ row }) => formatCurrency(row.cost),
      },
    ],
    []
  );

  const performanceColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: `${humanize(performanceView.slice(0, -1))} & ID`,
        minWidth: 220,
        flex: 1.5,
        sortable: false,
        compactValueGetter: (row) => `${row.name} ${row.entityId}`,
        renderCell: ({ row }) => (
          <div className="dashboard-primary-cell">
            <strong>{row.name}</strong>
            <small>{row.entityId}</small>
          </div>
        ),
      },
      {
        field: 'product',
        headerName: 'Product',
        minWidth: 150,
        flex: 1,
        sortable: false,
        compactValueGetter: (row) => row.product,
        renderCell: ({ row }) => row.product,
      },
      {
        field: 'mailed',
        headerName: performanceView === 'orders' ? 'Mailed' : 'Last mailed',
        minWidth: 150,
        flex: 1,
        sortable: false,
        compactValueGetter: (row) => row.mailed,
        renderCell: ({ row }) => row.mailed,
      },
      {
        field: 'mailedPieces',
        headerName: 'Mailed',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: true,
        compactValueGetter: (row) => formatNumber(row.mailedPieces),
        renderCell: ({ row }) => formatNumber(row.mailedPieces),
      },
      {
        field: 'spend',
        headerName: 'Spend',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: false,
        compactValueGetter: (row) => formatCurrency(row.spend),
        renderCell: ({ row }) => formatCurrency(row.spend),
      },
    ],
    [performanceView]
  );

  const lastRefreshed = mailData?.lastRefreshedAt
    ? new Date(mailData.lastRefreshedAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
  const gettingStarted = dashboardWidgets?.gettingStarted || {};
  const hasAddedContacts = gettingStarted.contactsAdded === true;
  const hasCreatedMailPiece = gettingStarted.mailPieceCreated === true;
  const hasPlacedOrder = gettingStarted.orderPlaced === true;

  return (
    <main className="mail-performance-dashboard" aria-busy={isLoading || isRefreshing}>
      {!isGettingStartedHidden ? (
        isLoading && !hadCachedDashboardDataOnMountRef.current ? (
          <section
            className="getting-started-card dashboard-getting-started-loading"
            aria-label="Loading setup guide"
            aria-busy="true"
          >
            <span className="dashboard-loading-line dashboard-loading-line--heading" />
            <span className="dashboard-loading-line dashboard-loading-line--copy" />
            <div className="dashboard-loading-guide-grid">
              <span />
              <span />
            </div>
          </section>
        ) : (
          <GettingStarted
            hasAddedContacts={hasAddedContacts}
            hasCreatedMailPiece={hasCreatedMailPiece}
            hasPlacedOrder={hasPlacedOrder}
            isLiveMode={isLiveMode}
            onHide={hideGettingStarted}
          />
        )
      ) : null}

      <header className="mail-performance-header">
        <div>
          <div className="mail-performance-title-row">
            <h1>Mail Performance</h1>
            {isGettingStartedHidden ? (
              <button
                type="button"
                className="time-pill setup-guide-button"
                aria-controls="getting-started"
                onClick={showGettingStarted}
              >
                <CheckBoxOutlinedIcon />
                <span>Setup guide</span>
              </button>
            ) : null}
          </div>
          <p>Track your mailings, delivery, and spend in one place.</p>
        </div>
        <div className="mail-performance-filters">
          <span>
            Last refreshed <strong>{lastRefreshed}</strong>
          </span>
          <OlcButton
            variant="secondary"
            iconOnly
            className={`refresh-button ${isRefreshing ? 'is-refreshing' : ''}`}
            aria-label="Refresh dashboard"
            onClick={() => loadDashboardData(true)}
            disabled={isRefreshing}
            startIcon={<RefreshRoundedIcon />}
          >
            Refresh
          </OlcButton>
          <div className="dashboard-date-control">
            {isCustomDateOpen ? (
              <div className="dashboard-date-picker mailedDate newDatePicker updatedDesign">
                <div className="selectDate">
                  <OlcDatePicker
                    open
                    calendarClassName="dashboard-date-calendar"
                    popperModifiers={[
                      {
                        name: 'offset',
                        // Recalculate bottom-end placement using the custom popover width.
                        options: { offset: [0, 0] },
                      },
                    ]}
                    popperPlacement="bottom-end"
                    popperWidth={244}
                    selectsRange
                    startDate={customDateRange[0]}
                    endDate={customDateRange[1]}
                    onChange={(nextRange) => {
                      setCustomDateRange(nextRange);
                      applyCustomDate(nextRange);
                    }}
                    onClickOutside={() => setIsCustomDateOpen(false)}
                    onCalendarClose={() => setIsCustomDateOpen(false)}
                    minDate={null}
                    toggleCalendarOnIconClick={true}
                    placeholderText="Select date range"
                    isClearable
                  />
                </div>
              </div>
            ) : (
              <div className="dashboard-date-dropdown" title={selectedDate.title}>
                <OlcDropdownSearch
                  options={dateOptions}
                  selectedValue={selectedDate}
                  setSelectedValue={(nextDate) => {
                    setSelectedDate(nextDate);
                    setIsCustomDateOpen(false);
                  }}
                  searchable={false}
                  placeHolderText="Select date"
                  component="overviewdateDropDown"
                  controlVariant="secondary"
                />
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="summary-grid" aria-label="Mail summary">
        <article className="spend-card">
          <span>Total spend</span>
          <strong>{isLoading ? '—' : formatCurrency(mailData?.spend)}</strong>
          <small>{formatNumber(mailPiecesSent)} mailpieces sent</small>
        </article>

        <article className="dashboard-card delivery-status-card">
          <div className="card-heading">
            <h2>Mail delivery status</h2>
            <span>
              of <strong>{formatNumber(mailPiecesSent)}</strong> sent
            </span>
          </div>
          <div className="delivery-bar" aria-label="Mail delivery status breakdown">
            <span
              className="delivered"
              style={{ width: `${deliveredPercent}%` }}
              title={`Delivered ${deliveredPercent.toFixed(1)}%`}
            >
              {deliveredPercent >= 18 ? `Delivered ${Math.round(deliveredPercent)}%` : ''}
            </span>
            <span
              className="in-transit"
              style={{ width: `${transitPercent}%` }}
              title={`In transit ${transitPercent.toFixed(1)}%`}
            />
            <span
              className="mailed"
              style={{ width: `${mailedPercent}%` }}
              title={`Mailed ${mailedPercent.toFixed(1)}%`}
            />
            <span
              className="returned"
              style={{ width: `${returnedPercent}%` }}
              title={`Returned ${returnedPercent.toFixed(1)}%`}
            />
          </div>
          <div className="delivery-legend">
            <span>
              <i className="legend-dot delivered" />
              Delivered <strong>{formatNumber(delivered)}</strong>
            </span>
            <span>
              <i className="legend-dot in-transit" />
              In transit <strong>{formatNumber(inTransit)}</strong>
            </span>
            <span>
              <i className="legend-dot mailed" />
              Mailed <strong>{formatNumber(mailed)}</strong>
            </span>
            <span>
              <i className="legend-dot returned" />
              Returned <strong>{formatNumber(returned)}</strong>
            </span>
          </div>
        </article>
      </section>

      <section className="insight-grid" aria-label="Mail insights">
        <article className="dashboard-card product-mix-card">
          <h2>Product mix</h2>
          {productMix.entries.length ? (
            <div className="product-mix-content">
              <div className="product-donut" style={{ background: productMix.background }}>
                <span />
              </div>
              <div className="product-legend">
                {productMix.entries.map((item) => (
                  <div key={item.name}>
                    <i style={{ background: item.color }} />
                    <span>{item.name}</span>
                    <strong>{Math.round(item.percentage)}%</strong>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card-empty-state">
              <strong>No mail sent yet</strong>
              <span>Your product breakdown appears once you&apos;ve mailed.</span>
            </div>
          )}
        </article>

        <article className="dashboard-card delivery-speed-card">
          <h2>Delivery speed</h2>
          {deliverySpeedPieces ? (
            <div className="delivery-speed-content">
              <div className="delivery-speed-primary">
                <strong>{formatAverageValue(deliverySpeed.overall.averageDays)}</strong>
                <span>days</span>
              </div>
              <p className="delivery-speed-description">
                average to reach the mailbox
                <span aria-hidden="true">·</span>
                {Math.round(Number(deliverySpeed.overall.withinEightDaysPercent) || 0)}% within 8
                days
              </p>
              <div className="delivery-speed-breakdown">
                <div>
                  <span>First class</span>
                  <strong>{formatAverageDays(deliverySpeed.firstClass?.averageDays)}</strong>
                </div>
                <div>
                  <span>Standard class</span>
                  <strong>{formatAverageDays(deliverySpeed.standardClass?.averageDays)}</strong>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-empty-state">
              <strong>No delivered mail yet</strong>
              <span>Delivery timing appears after eligible pieces are marked delivered.</span>
            </div>
          )}
        </article>

        <article className="dashboard-card address-quality-card">
          <h2>Address quality</h2>
          <div className="quality-removed">
            <div className="quality-label">
              Removed before mailing
              <span className="quality-shield" aria-hidden="true">
                <GppGoodOutlinedIcon />
              </span>
              <OlcTooltip
                classes={DASHBOARD_TOOLTIP_CLASSES}
                title="Undeliverable addresses caught by address checks when you placed orders — you weren't charged to send them."
              >
                <img
                  className="quality-info-icon"
                  src={ContactInfoCIcon}
                  alt="About removed before mailing"
                />
              </OlcTooltip>
            </div>
            <div className="quality-removed-value">
              <div>
                <strong>{formatNumber(removedBeforeDelivery)}</strong>
                <span>{removedBeforeDelivery === 1 ? 'address' : 'addresses'}</span>
              </div>
              <em>≈ {formatCurrency(estimatedAddressSavings)} saved</em>
            </div>
          </div>
          <div className="quality-returned">
            <div className="quality-label">
              Returned after delivery
              <OlcTooltip
                classes={DASHBOARD_TOOLTIP_CLASSES}
                title="Returned pieces from completed orders as a percentage of all mailpieces sent."
              >
                <img
                  className="quality-info-icon"
                  src={ContactInfoCIcon}
                  alt="About returned after delivery"
                />
              </OlcTooltip>
            </div>
            <div className="quality-value">
              <strong>{returnRate.toFixed(1)}%</strong>
              <span>{formatNumber(returned)} pieces</span>
              <em className={returnRateLevel.toLowerCase()}>{returnRateLevel}</em>
            </div>
            <div className="quality-band">
              <span className="normal" />
              <span className="fill" style={{ width: `${Math.min(returnRate * 5, 100)}%` }} />
              <span className="marker" style={{ left: `${Math.min(returnRate * 5, 100)}%` }} />
            </div>
          </div>
        </article>
      </section>

      <section className="dashboard-table-section">
        <div className="section-heading">
          <div>
            <div className="heading-row">
              <Tabs
                value={ordersView}
                onChange={(_, nextView) => {
                  setOrdersView(nextView);
                  setOrdersPage(1);
                }}
                className="dashboard-text-tabs dashboard-order-tabs"
                aria-label="Order timeframe"
                TabIndicatorProps={{ className: 'dashboard-tab-indicator' }}
              >
                <Tab
                  value="active"
                  label="Active orders"
                  className="dashboard-text-tab"
                  disableRipple
                />
                <Tab
                  value="future"
                  label="Future orders"
                  className="dashboard-text-tab"
                  disableRipple
                />
              </Tabs>
              <span className="time-pill">All time</span>
            </div>
            <p className="dashboard-order-description">
              {ordersView === 'active'
                ? "What's currently in production, in transit, or recently delivered."
                : 'Orders scheduled to mail in the future.'}
            </p>
          </div>
          <div className="section-actions">
            <OlcButton
              type="button"
              variant="secondary"
              size="small"
              onClick={() => navigate('/orders')}
            >
              View all orders
            </OlcButton>
          </div>
        </div>

        <div className="dashboard-grid-shell">
          <OlcDataGrid
            rows={visibleOrders}
            columns={orderColumns}
            loading={isLoading}
            hideFooter
            rowSelection={false}
            disableRowSelectionOnClick
            disableColumnMenu
            rowHeight={61}
            columnHeaderHeight={48}
            getRowId={(row) => row.id}
            onRowClick={({ id }) => navigate(`/orders/detail/overview/${id}`)}
            slots={{
              noRowsOverlay: () => (
                <div className="dashboard-grid-empty">
                  {ordersView === 'active'
                    ? 'No active orders right now.'
                    : 'No future orders scheduled.'}
                </div>
              ),
            }}
          />
          {(isLoading || selectedOrders.length > 0) && (
            <OlcTablePagination
              count={ordersPageCount}
              page={ordersPage}
              pageSize={DASHBOARD_PAGE_SIZE}
              onPageChange={(_, nextPage) => setOrdersPage(nextPage)}
              from={(ordersPage - 1) * DASHBOARD_PAGE_SIZE + 1}
              to={ordersPage * DASHBOARD_PAGE_SIZE}
              total={selectedOrders.length}
              loading={isLoading}
              showPageSize={false}
            />
          )}
        </div>
      </section>

      <section className="dashboard-table-section performance-section">
        <div className="section-heading performance-heading">
          <div>
            <div className="heading-row">
              <h2>Most mailed</h2>
              <span className="time-pill">All time</span>
            </div>
            <p>
              Your highest-volume sends — the orders, templates, and campaigns you mail the most.
            </p>
          </div>
          <Tabs
            value={performanceView}
            onChange={(_, nextView) => {
              setPerformanceView(nextView);
              setPerformancePage(1);
            }}
            className="dashboard-text-tabs dashboard-performance-tabs"
            aria-label="Performance type"
            TabIndicatorProps={{ className: 'dashboard-tab-indicator' }}
          >
            {['templates', 'orders', 'campaigns'].map((view) => (
              <Tab
                key={view}
                value={view}
                label={humanize(view)}
                className="dashboard-text-tab"
                disableRipple
              />
            ))}
          </Tabs>
        </div>

        <div className="dashboard-grid-shell">
          <OlcDataGrid
            rows={mostMailedRows}
            columns={performanceColumns}
            loading={isLoading}
            hideFooter
            rowSelection={false}
            disableRowSelectionOnClick
            disableColumnMenu
            rowHeight={61}
            columnHeaderHeight={48}
            getRowId={(row) => row.id}
            initialState={{
              sorting: {
                sortModel: [{ field: 'mailedPieces', sort: 'desc' }],
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <div className="dashboard-grid-empty">
                  No {performanceView} have been mailed yet.
                </div>
              ),
            }}
          />
          {(isLoading || rankedPerformanceRows.length > 0) && (
            <OlcTablePagination
              count={performancePageCount}
              page={performancePage}
              pageSize={DASHBOARD_PAGE_SIZE}
              onPageChange={(_, nextPage) => setPerformancePage(nextPage)}
              from={(performancePage - 1) * DASHBOARD_PAGE_SIZE + 1}
              to={performancePage * DASHBOARD_PAGE_SIZE}
              total={rankedPerformanceRows.length}
              loading={isLoading}
              showPageSize={false}
            />
          )}
        </div>
      </section>
    </main>
  );
};

export default Dashboard;
